import React from 'react';
import { SelectValue } from 'antd/lib/select';
import { useTranslation } from 'react-i18next';

import { GuardrailSettingsState, RegexFlag, RegexMaskingDetectorSettings } from '../../../../../gamma-api';
import IbInput from '../../../../components/common/IbInput';
import IbSelect from '../../../../components/common/IbSelect';
import IbTypography from '../../../../components/common/IbTypography';
import RegexPatternsEditor from '../../../../components/common/RegexPatternsEditor';
import { ITEM_CLASS_NAME } from '../../../../components/common/TypedStepModal/const';
import { UseValidationForm } from '../../../../utils/validationUtil';
import { getRegexFlagLabel, GuardrailDetectorSettings, IMaskingDetectorStep } from '../shared/utils';

import { DetectorValidationField, DetectorValidationForm } from './DetectorModal.validation';
import CommonDetectorParameters from './CommonDetectorParameters';

export interface IRegexDetectorParametersProps {
  step: IMaskingDetectorStep;
  validationFieldRefs: Partial<Record<DetectorValidationField, React.RefObject<HTMLDivElement>>>;
  validationForm: UseValidationForm<DetectorValidationForm>;
  onChange: (patch: Partial<GuardrailDetectorSettings>) => void;
}

const RegexDetectorParameters: React.FC<IRegexDetectorParametersProps> = ({
  step,
  validationFieldRefs,
  validationForm,
  onChange,
}) => {
  const { t } = useTranslation();
  const detector = step.detector as RegexMaskingDetectorSettings;
  const { validationState, onFieldChange, onFieldBlur, getShowError, getErrorMessage } = validationForm;
  const showPatternsError = getShowError(validationState.patterns);

  const flagOptions = [RegexFlag.IgnoreCase, RegexFlag.Multiline, RegexFlag.DotAll, RegexFlag.Ascii].map((value) => ({
    value,
    label: getRegexFlagLabel(value),
  }));

  const onStateChange = (state: GuardrailSettingsState) => {
    onChange({ state });
    onFieldChange('state', state);
  };

  const onPatternsChange = (patterns: string[]) => {
    onChange({ patterns });
    onFieldChange('patterns', patterns);
  };

  const onFlagsChange = (value: SelectValue) => {
    const flags = value as RegexFlag[];
    onChange({ flags });
    onFieldChange('flags', flags);
  };

  const onPatternsBlur = () => onFieldBlur('patterns');

  const onEntityTypeChange = (value: string) => {
    onChange({ entity_type: value });
  };

  return (
    <>
      <CommonDetectorParameters detector={step.detector} onChange={onChange} onStateChange={onStateChange} />
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: Masking: Detector: Regex: EntityType')}
        </IbTypography.Paragraph>
        <IbInput value={detector.entity_type} onChange={onEntityTypeChange} />
      </div>
      <div ref={validationFieldRefs.patterns} className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: RegexCheck: Patterns: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: RegexCheck: Patterns: Description')}
        </IbTypography.Paragraph>
        <RegexPatternsEditor
          hasError={showPatternsError}
          patterns={detector.patterns}
          onBlur={onPatternsBlur}
          onChange={onPatternsChange}
        />
        {showPatternsError && (
          <IbTypography.Paragraph error type="descriptor">
            {getErrorMessage(validationState.patterns, t('Agent: Guardrails: RegexCheck: Patterns: Error'))}
          </IbTypography.Paragraph>
        )}
      </div>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: RegexCheck: Flags: Title')}
        </IbTypography.Paragraph>
        <IbSelect mode="multiple" options={flagOptions} value={detector.flags} onChange={onFlagsChange} />
      </div>
    </>
  );
};

export default RegexDetectorParameters;
